import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Save, Delete, Edit } from '@mui/icons-material';
import { siteStatService, extractApiDataList } from '../services/api';

const emptyForm = {
  stat_value: '',
  stat_label: '',
  display_order: '0',
  is_active: '1',
};

const SiteStatsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyForm);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await siteStatService.getAll({ include_inactive: 1 });
      setRows(extractApiDataList(res));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load stats';
      setLoadError(msg);
      setRows([]);
      setToast({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!newRow.stat_value.trim() || !newRow.stat_label.trim()) {
      setToast({ open: true, message: 'Value and label are required', type: 'error' });
      return;
    }
    try {
      await siteStatService.create({
        stat_value: newRow.stat_value.trim(),
        stat_label: newRow.stat_label.trim(),
        display_order: Number(newRow.display_order) || 0,
        is_active: newRow.is_active === '1' ? 1 : 0,
      });
      setNewRow(emptyForm);
      setToast({ open: true, message: 'Stat added', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditRow({
      stat_value: row.stat_value || '',
      stat_label: row.stat_label || '',
      display_order: String(row.display_order ?? 0),
      is_active: row.is_active === 0 || row.is_active === false || row.is_active === '0' ? '0' : '1',
    });
  };

  const handleSaveEdit = async () => {
    if (!editId || !editRow.stat_value.trim() || !editRow.stat_label.trim()) return;
    try {
      await siteStatService.update(editId, {
        stat_value: editRow.stat_value.trim(),
        stat_label: editRow.stat_label.trim(),
        display_order: Number(editRow.display_order) || 0,
        is_active: editRow.is_active === '1' ? 1 : 0,
      });
      setEditId(null);
      setToast({ open: true, message: 'Stat updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this stat?')) return;
    try {
      await siteStatService.delete(id);
      setToast({ open: true, message: 'Stat deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Site Stats
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Manage the Join With Us counters shown on the public site (for example 10M, 20k, 300, 22).
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add new stat
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Value"
                placeholder="10M"
                value={newRow.stat_value}
                onChange={(e) => setNewRow({ ...newRow, stat_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Label"
                placeholder="Total donation"
                value={newRow.stat_label}
                onChange={(e) => setNewRow({ ...newRow, stat_label: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Order"
                value={newRow.display_order}
                onChange={(e) => setNewRow({ ...newRow, display_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <TextField
                select
                fullWidth
                size="small"
                label="Active"
                value={newRow.is_active}
                onChange={(e) => setNewRow({ ...newRow, is_active: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {editId === row.id ? (
                        <TextField size="small" value={editRow.stat_value} onChange={(e) => setEditRow({ ...editRow, stat_value: e.target.value })} />
                      ) : row.stat_value}
                    </TableCell>
                    <TableCell>
                      {editId === row.id ? (
                        <TextField size="small" fullWidth value={editRow.stat_label} onChange={(e) => setEditRow({ ...editRow, stat_label: e.target.value })} />
                      ) : row.stat_label}
                    </TableCell>
                    <TableCell>
                      {editId === row.id ? (
                        <TextField size="small" type="number" sx={{ width: 90 }} value={editRow.display_order} onChange={(e) => setEditRow({ ...editRow, display_order: e.target.value })} />
                      ) : row.display_order}
                    </TableCell>
                    <TableCell>
                      {editId === row.id ? (
                        <TextField
                          select
                          size="small"
                          value={editRow.is_active}
                          onChange={(e) => setEditRow({ ...editRow, is_active: e.target.value })}
                          SelectProps={{ native: true }}
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </TextField>
                      ) : (
                        <Chip
                          size="small"
                          label={row.is_active === 0 || row.is_active === false || row.is_active === '0' ? 'Inactive' : 'Active'}
                          color={row.is_active === 0 || row.is_active === false || row.is_active === '0' ? 'default' : 'success'}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {editId === row.id ? (
                        <>
                          <IconButton color="success" onClick={handleSaveEdit}><Save /></IconButton>
                          <Button size="small" onClick={() => setEditId(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <IconButton color="primary" onClick={() => startEdit(row)}><Edit /></IconButton>
                          <IconButton color="error" onClick={() => handleDelete(row.id)}><Delete /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#667085' }}>
                      No stats yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SiteStatsPage;
